import React, { useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const VerifyEmailScreen = () => {
  const [searchParams] = useSearchParams();
  const { token: pathToken } = useParams();
  const navigate = useNavigate();
  // 'pending' — waiting for user to click confirm
  // 'loading' — POST in flight
  // 'success' — verified
  // 'error'   — token missing, invalid, or expired
  const [status, setStatus] = useState('pending');
  const [errorMessage, setErrorMessage] = useState('');

  const token = pathToken || searchParams.get('token');

  const handleConfirm = () => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token found. Please check your email link.');
      return;
    }

    setStatus('loading');

    fetch(`${API_URL}/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data.success) {
          setStatus('success');
          setTimeout(() => navigate('/login?verified=true'), 2500);
        } else {
          setStatus('error');
          setErrorMessage(data.error || 'Verification failed. The link may have expired.');
        }
      })
      .catch(() => {
        setStatus('error');
        setErrorMessage('Unable to reach the server. Please try again.');
      });
  };

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f7fafc',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
  };

  const cardStyle = {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    padding: '48px 40px',
    maxWidth: '440px',
    width: '90%',
    textAlign: 'center'
  };

  const logoStyle = {
    fontSize: '28px',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '32px'
  };

  const confirmBtnStyle = {
    display: 'inline-block',
    background: '#2563eb',
    color: 'white',
    borderRadius: '8px',
    padding: '12px 28px',
    fontWeight: '600',
    fontSize: '15px',
    border: 'none',
    cursor: 'pointer',
    marginTop: '8px'
  };

  if (status === 'pending') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={logoStyle}>🎸 AmpNebula</div>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✉️</div>
          <h2 style={{ color: '#2d3748', marginBottom: '8px' }}>Confirm your email</h2>
          <p style={{ color: '#718096', marginBottom: '28px' }}>
            Click the button below to verify your email address and activate your account.
          </p>
          {!token ? (
            <p style={{ color: '#c53030' }}>
              No verification token found. Please use the link from your confirmation email.
            </p>
          ) : (
            <button style={confirmBtnStyle} onClick={handleConfirm}>
              Confirm Email Address
            </button>
          )}
        </div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={logoStyle}>🎸 AmpNebula</div>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <h2 style={{ color: '#2d3748', marginBottom: '8px' }}>Verifying your email…</h2>
          <p style={{ color: '#718096' }}>Just a moment.</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={logoStyle}>🎸 AmpNebula</div>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
          <h2 style={{ color: '#276749', marginBottom: '8px' }}>Email verified!</h2>
          <p style={{ color: '#718096', marginBottom: '24px' }}>
            Your account is confirmed. Redirecting you to sign in…
          </p>
          <a
            href="/login?verified=true"
            style={{
              display: 'inline-block',
              background: '#2563eb',
              color: 'white',
              borderRadius: '8px',
              padding: '10px 24px',
              fontWeight: '600',
              textDecoration: 'none',
              fontSize: '14px'
            }}
          >
            Go to Sign In
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={logoStyle}>🎸 AmpNebula</div>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
        <h2 style={{ color: '#c53030', marginBottom: '8px' }}>Verification failed</h2>
        <p style={{ color: '#718096', marginBottom: '24px' }}>{errorMessage}</p>
        <a
          href="/login"
          style={{
            display: 'inline-block',
            background: '#2563eb',
            color: 'white',
            borderRadius: '8px',
            padding: '10px 24px',
            fontWeight: '600',
            textDecoration: 'none',
            fontSize: '14px',
            marginRight: '12px'
          }}
        >
          Go to Sign In
        </a>
        <a
          href="/login#resend"
          style={{
            display: 'inline-block',
            background: '#e2e8f0',
            color: '#4a5568',
            borderRadius: '8px',
            padding: '10px 24px',
            fontWeight: '600',
            textDecoration: 'none',
            fontSize: '14px'
          }}
        >
          Resend email
        </a>
      </div>
    </div>
  );
};

export default VerifyEmailScreen;

